// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react'
// import _ from 'lodash'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import { PlusIcon, PencilSquareIcon, TrashIcon, QrCodeIcon, DocumentArrowDownIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline'

import { useAuth } from 'auth/AuthContext'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import PrintModal from 'components/modals/PrintModal'
import QRModal from 'components/modals/QRModal'
import WorkerModal from 'components/modals/WorkerModal'
import ButtonComponent from 'components/common/ButtonComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import TableComponent from 'components/common/TableComponent'
import UploadWorkersModal from 'components/modals/UploadWorkersModal'
import PageLoadingState from 'components/common/PageLoadingState'

const WorkersManagement = () => {
  const hasFetched = useRef(false)
  const { user } = useAuth()

  const [state, setState] = useState({
    searchTerm: '',
    workers: [],
    selectedWorker: null,
    selectedWorkerForQR: null,
    totals: {
      totalWorkers: 0,
      totalActive: 0,
      totalInactive: 0
    },
    isWorkerModalOpen: false,
    isQRModalOpen: false,
    isPrintModalOpen: false,
    isOpenWorkersModal: false,
    loading: true
  })

  const getWorkers = async () => {
    // uncomment when backend returns last entity created
    // if (hasFetched.current) return
    // hasFetched.current = true

    const response = await makeRequest({
      method: 'get',
      url: `/management/workers-management/${user.farmId}/all`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })

    if (response) {
      setState((s) => ({
        ...s,
        workers: response.data
      }))
    }
  }

  const getTotals = async () => {
    const response = await makeRequest({
      method: 'get',
      url: `/management/workers-management/totals/${user.farmId}`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })

    if (response) {
      setState((s) => ({
        ...s,
        totals: response.data
      }))
    }
  }

  const openAddWorkerModal = () => {
    setState((s) => ({
      ...s,
      selectedWorker: {},
      isWorkerModalOpen: true
    }))
  }

  const openModalForEdit = (worker) => {
    setState((s) => ({
      ...s,
      selectedWorker: worker,
      isWorkerModalOpen: true
    }))
  }

  const closeModal = () => {
    setState((s) => ({
      ...s,
      selectedWorker: null,
      isWorkerModalOpen: false
    }))
  }

  const handleSave = async (workerData) => {
    const isEditing = Object.keys(state.selectedWorker).length > 0

    const response = await makeRequest({
      method: isEditing ? 'put' : 'post',
      url: isEditing
        ? `/management/workers-management/${workerData.workerId}`
        : '/management/workers-management',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      },
      data: {
        farmId: user.farmId,
        ...workerData
      },
      alertResult: true
    })

    if (response.status === 200) {
      closeModal()
      getWorkers()
      getTotals()
    }
  }

  const handleDelete = async (workerId) => {
    const response = await makeRequest({
      method: 'patch',
      url: `/management/workers-management/delete-worker/${workerId}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`,
        'Content-Type': 'application/json'
      },
      data: {},
      alertResult: true
    })

    if (response.status === 200) {
      setState((s) => ({
        ...s,
        selectedWorker: null,
        isWorkerModalOpen: false
      }))
      closeModal()
    }
  }

  const handleQRClick = (worker) => {
    setState(s => ({
      ...s,
      selectedWorkerForQR: worker,
      isQRModalOpen: true
    }))
  }

  const handlePrintClick = (worker) => {
    setState(s => ({
      ...s,
      selectedWorkerForQR: worker,
      isPrintModalOpen: true
    }))
  }

  const handleExportExcel = () => {
    try {
      const dataToExport = state.workers.map(worker => ({
        ID: worker.workerId,
        Name: worker.firstName,
        'Last Name': worker.lastName,
        Status: worker.isActive ? 'Active' : 'Suspended',
        'Created At': worker.createdAt
      }))

      const worksheet = XLSXUtils.json_to_sheet(dataToExport)
      const workbook = XLSXUtils.book_new()
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Workers')

      // Ajustar el ancho de las columnas
      const maxWidth = dataToExport.reduce((w, r) => Math.max(w, r.Name.length), 10)
      worksheet['!cols'] = [
        { wch: 10 }, // ID
        { wch: maxWidth }, // Name
        { wch: maxWidth }, // Last Name
        { wch: 15 }, // Status
        { wch: 15 }, // Phone
        { wch: 25 }, // Email
        { wch: 15 }, // Role
        { wch: 20 } // Created At
      ]

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      })

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `workers_${new Date().toISOString().split('T')[0]}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)

      showAlert({
        text: 'Excel file downloaded successfully',
        icon: 'success'
      })
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      showAlert({
        text: 'Failed to export Excel file',
        icon: 'error'
      })
    }
  }

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF()

      doc.setFontSize(16)
      doc.text('Workers Report', 14, 15)
      doc.setFontSize(10)
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 25)

      const tableColumn = [
        'ID',
        'Name',
        'Last Name',
        'Status',
        'Phone',
        'Email',
        'Role',
        'Created At'
      ]

      const tableRows = state.workers.map(worker => [
        worker.workerId,
        worker.firstName,
        worker.lastName,
        worker.isActive ? 'Active' : 'Suspended',
        '',
        '',
        worker.createdAt
      ])

      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255
        }
      })

      doc.save(`workers_${new Date().toISOString().split('T')[0]}.pdf`)

      showAlert({
        text: 'PDF file downloaded successfully',
        icon: 'success'
      })
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      showAlert({
        text: 'Failed to export PDF file',
        icon: 'error'
      })
    }
  }

  const filteredWorkers = state.workers.filter(worker => {
    const fullName = `${worker?.firstName ?? ''} ${worker?.lastName ?? ''}`.toLowerCase()
    const oldId = worker.oldId ?? ''
    const status = worker.isActive ? 'active' : 'suspended'

    return (
      fullName.includes(state.searchTerm) ||
      status.includes(state.searchTerm) ||
      oldId.includes(state.searchTerm)
    )
  })

  const rowsFiltered = filteredWorkers.map(worker => ({
    ...worker,
    status: worker.isActive ? 'Active' : 'Suspended',
    fullName: `${worker.firstName} ${worker.lastName}`
  }))

  const workersTableData = {
    columns: [
      {
        key: 'workerId',
        label: '#'
      },
      {
        key: 'oldId',
        label: 'Pick Id'
      },
      {
        key: 'fullName',
        label: 'Full Name'
      },
      {
        key: 'status',
        label: 'Worker Status',
        cell: status => (
          <span className={status === 'Active' ? 'text-green-600' : 'text-red-600'}>
            {status}
          </span>
        )
      },
      {
        key: 'createdAt',
        label: 'Creation Date Time',
        cell: createdAt => parseDate(createdAt)
      },
      {
        key: 'actions',
        label: 'Manage',
        cell: (value, worker) => (
          <div className='flex items-center gap-2'>
            <button onClick={() => handleQRClick(worker)} title='QR' className='p-1 rounded transition'>
              <QrCodeIcon className='w-5 h-5 text-gray-600' />
            </button>
            <button onClick={() => openModalForEdit(worker)} title='Edit' className='p-1 rounded transition'>
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDelete(worker.workerId)} title='Delete' className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: rowsFiltered
  }

  const onToggleUploadCsvModal = () => {
    setState(s => ({
      ...s,
      isOpenWorkersModal: !s.isOpenWorkersModal
    }))
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    Promise.all([
      getWorkers(),
      getTotals()
    ]).finally(() => {
      setState(s => ({ ...s, loading: false }))
    })
  }, [user?.farmId])

  if (state.loading) {
    return <PageLoadingState message='Loading workers...' rows={8} />
  }

  return (
    <>
      <div className='grid grid-cols-3 gap-6'>
        <div className='mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50 text-center'>
          <h2 className='text-xl font-bold text-green-700 mb-2'>{state.totals.totalWorkers}</h2>
          <p>Total Workers</p>
        </div>
        <div className='mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50 text-center'>
          <h2 className='text-xl font-bold text-green-700 mb-2'>{state.totals.totalActive}</h2>
          <p>Active Workers</p>
        </div>
        <div className='mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50 text-center'>
          <h2 className='text-xl font-bold text-green-700 mb-2'>{state.totals.totalInactive}</h2>
          <p>Suspended Workers</p>
        </div>
      </div>

      <ActionCardComponent>
        <input
          type='text'
          value={state.searchTerm}
          placeholder='Filter Workers'
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='search-input'
          data-align='left'
        />
        <ButtonComponent onClick={() => setState(s => ({ ...s, isOpenWorkersModal: true }))}>
          <DocumentArrowUpIcon className='w-5 h-5' />
          <span className='font-medium'>Import Workers</span>
        </ButtonComponent>
        <ButtonComponent onClick={openAddWorkerModal}>
          <PlusIcon className='w-5 h-5 mr-1' />
          New Worker
        </ButtonComponent>
        <ButtonComponent onClick={handlePrintClick}>
          <QrCodeIcon className='w-5 h-5' />
          Print Worker Cards
        </ButtonComponent>
        <ButtonComponent onClick={handleExportExcel} className='flex items-center gap-2'>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>Export as Excel</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportPDF} className='flex items-center gap-2'>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>Export as PDF</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={workersTableData} />

      <WorkerModal
        isOpen={state.isWorkerModalOpen}
        onClose={() => setState(s => ({ ...s, isWorkerModalOpen: false }))}
        worker={state.selectedWorker}
        onSave={handleSave}
      />
      <PrintModal
        isOpen={state.isPrintModalOpen}
        onClose={() => setState(s => ({ ...s, isPrintModalOpen: false }))}
        workers={state.workers}
      />
      <QRModal
        isOpen={state.isQRModalOpen}
        onClose={() => setState(s => ({ ...s, isQRModalOpen: false }))}
        worker={state.selectedWorkerForQR}
      />
      <UploadWorkersModal
        isOpen={state.isOpenWorkersModal}
        onClose={onToggleUploadCsvModal}
      />
    </>
  )
}

export default WorkersManagement
